'use client';
/**
 * BROUILLON LOCAL d'un écran de saisie (RQ, 19/08/2026).
 *
 * Le cas vécu : on est au milieu d'une série de CQ ou d'une déclaration de NC,
 * on clique par mégarde ailleurs, et au retour tout est vide. La saisie non
 * enregistrée doit survivre à la navigation.
 *
 * ── Choix qui engagent ──────────────────────────────────────────────────────
 *
 *  · localStorage, PAS la base : un brouillon de saisie est un état de POSTE DE
 *    TRAVAIL. L'envoyer au serveur en ferait une donnée du laboratoire, avec
 *    des questions (qui le voit ? s'audite-t-il ?) qu'un simple filet de
 *    sécurité n'a pas à poser.
 *  · Clé PAR UTILISATEUR et par écran : deux personnes qui se relaient sur le
 *    même poste ne doivent pas hériter du brouillon l'une de l'autre.
 *  · PÉREMPTION 24 h : un brouillon d'avant-hier restauré sans prévenir ferait
 *    ressaisir des valeurs périmées — pire que la page vide.
 *  · La restauration est PROPOSÉE, jamais imposée : un bandeau dit qu'un
 *    brouillon existe et de quand il date ; on le reprend ou on l'écarte.
 *  · `purger()` à l'enregistrement RÉUSSI — un brouillon qui ressuscite des
 *    données déjà en base ferait double saisie.
 */
import React from 'react';

const AGE_MAX_MS = 24 * 3600 * 1000;

type Enveloppe<T> = { quand: number; valeurs: T };

export function useBrouillonLocal<T>(ecran: string, utilisateur: string, valeurs: T, actif = true): {
  /** Brouillon disponible à la reprise — `null` si aucun (ou périmé). */
  propose: { quand: number; valeurs: T } | null;
  reprendre: () => T | null;
  ecarter: () => void;
  purger: () => void;
} {
  const cle = `bio24.saisie.${utilisateur || 'anonyme'}.${ecran}`;
  const [propose, setPropose] = React.useState<Enveloppe<T> | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const brut = localStorage.getItem(cle);
      const env = brut ? JSON.parse(brut) as Enveloppe<T> : null;
      if (!env || Date.now() - env.quand > AGE_MAX_MS) return null;
      return env;
    } catch { return null; }
  });

  // Sauvegarde CONTINUE, amortie : à chaque accalmie de 800 ms, pas à chaque
  // frappe — écrire le JSON complet à chaque caractère ferait ramer la saisie
  // qu'on cherche justement à protéger.
  const minuteur = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(() => {
    if (!actif) return undefined;
    if (minuteur.current) clearTimeout(minuteur.current);
    minuteur.current = setTimeout(() => {
      try {
        // Un formulaire VIDE n'écrase pas un brouillon existant : au premier
        // rendu, avant restauration, les valeurs sont vierges.
        const aQuelqueChose = JSON.stringify(valeurs).replace(/[{}\[\]":,]/g, '').trim().length > 0;
        if (aQuelqueChose) localStorage.setItem(cle, JSON.stringify({ quand: Date.now(), valeurs }));
      } catch { /* stockage plein ou privé : le filet disparaît, la saisie continue */ }
    }, 800);
    return () => { if (minuteur.current) clearTimeout(minuteur.current); };
  }, [valeurs, cle, actif]);

  const purger = React.useCallback(() => {
    try { localStorage.removeItem(cle); } catch { /* privé */ }
    setPropose(null);
  }, [cle]);

  return {
    propose,
    reprendre: () => { const v = propose?.valeurs ?? null; setPropose(null); return v; },
    ecarter: purger,
    purger,
  };
}

/** Le bandeau de reprise — le même sur tous les écrans protégés. */
export function BandeauBrouillon({ propose, onReprendre, onEcarter }: {
  propose: { quand: number } | null;
  onReprendre: () => void;
  onEcarter: () => void;
}) {
  if (!propose) return null;
  const heure = new Date(propose.quand).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return React.createElement('div', {
    role: 'status',
    style: {
      display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
      padding: '8px 12px', borderRadius: 8, fontSize: 12.5,
      background: 'var(--cyan-50, #ECFEFF)', border: '1px solid var(--cyan-200, #A5F3FC)',
    },
  },
  React.createElement('span', null, `Une saisie non enregistrée de ${heure} a été retrouvée sur ce poste.`),
  React.createElement('button', {
    className: 'ged-btn', onClick: onReprendre,
    style: { fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid var(--cyan-200)', borderRadius: 6, padding: '4px 10px', background: '#fff' },
  }, 'Reprendre'),
  React.createElement('button', {
    className: 'ged-btn', onClick: onEcarter,
    style: { fontSize: 12, cursor: 'pointer', border: 'none', background: 'none', color: 'var(--text-muted)', textDecoration: 'underline' },
  }, 'Écarter'));
}
